import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, MapPin, Tag } from 'lucide-react';
import { api, getMunicipioSlug } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import { resolveBackendImage } from '../utils/brandLogo';

/**
 * SearchAutocomplete — barra de busca com sugestões em tempo real.
 *
 * Props:
 *   - placeholder: string
 *   - onSubmit?: (q: string) => void  (default: navigate {tenant}/explorar?busca=...)
 *   - municipioSlug?: string (default: getMunicipioSlug())
 *   - className?: string
 *
 * Endpoint: GET /api/v2/businesses/sugestoes?q=...&municipio_slug=...
 */
export default function SearchAutocomplete({
  placeholder = 'O que você procura?',
  onSubmit,
  municipioSlug,
  className = '',
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ businesses: [], categorias: [], bairros: [] });
  const containerRef = useRef(null);
  const debounced = useDebounce(q, 300);
  const slug = municipioSlug || getMunicipioSlug();

  // fetch sugestões com debounce
  useEffect(() => {
    if (!debounced || debounced.trim().length < 2) {
      setData({ businesses: [], categorias: [], bairros: [] });
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.get('/v2/businesses/sugestoes', {
      params: { q: debounced.trim(), municipio_slug: slug, limit: 6 },
    })
      .then((r) => {
        if (!cancelled) setData(r.data || { businesses: [], categorias: [], bairros: [] });
      })
      .catch(() => { if (!cancelled) setData({ businesses: [], categorias: [], bairros: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debounced, slug]);

  // click outside fecha o dropdown
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setOpen(false);
    if (onSubmit) onSubmit(term);
    else {
      const base = slug ? `/${slug}` : '';
      navigate(`${base}/explorar?busca=${encodeURIComponent(term)}`);
    }
  }

  function navBiz(biz) {
    setOpen(false);
    setQ('');
    navigate(`/negocio/${biz.slug || biz.id}`);
  }

  function navCat(cat) {
    setOpen(false);
    setQ('');
    const base = slug ? `/${slug}` : '';
    navigate(`${base}/explorar?categoria=${encodeURIComponent(cat)}`);
  }

  function navBairro(bairro) {
    setOpen(false);
    setQ('');
    const base = slug ? `/${slug}` : '';
    navigate(`${base}/explorar?bairro=${encodeURIComponent(bairro)}`);
  }

  const hasResults =
    data.businesses?.length > 0 ||
    data.categorias?.length > 0 ||
    data.bairros?.length > 0;

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid="search-autocomplete">
      <form onSubmit={handleSubmit} className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={open && (hasResults || loading)}
          aria-autocomplete="list"
          aria-controls="autocomplete-listbox"
          data-testid="search-autocomplete-input"
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </form>

      {/* dropdown */}
      {open && q.trim().length >= 2 && (hasResults || loading) && (
        <div
          id="autocomplete-listbox"
          role="listbox"
          className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-lg border border-gray-200 shadow-xl overflow-hidden max-h-96 overflow-y-auto"
          data-testid="search-autocomplete-list"
        >
          {/* negócios */}
          {data.businesses?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase font-semibold text-gray-500 bg-gray-50">Negócios</div>
              {data.businesses.map((b) => {
                // `imagens.logo` pode vir como objeto {thumb, medium, large}
                // (novo pipeline de upload) ou string (legado). Se for
                // objeto, extrai o menor tamanho disponível.
                const logoObj = b.imagens?.logo;
                const logoFromObj = (logoObj && typeof logoObj === 'object')
                  ? (logoObj.thumb || logoObj.medium || logoObj.large || null)
                  : (typeof logoObj === 'string' ? logoObj : null);
                const foto = (typeof b.logo_url === 'string' && b.logo_url)
                  ? b.logo_url
                  : (logoFromObj || (typeof b.avatar_url === 'string' ? b.avatar_url : null));
                return (
                  <button
                    key={b.id}
                    role="option"
                    type="button"
                    onClick={() => navBiz(b)}
                    data-testid={`autocomplete-biz-${b.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-purple-50 transition flex items-center gap-2"
                  >
                    {foto ? (
                      <img src={resolveBackendImage(foto)} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-xs">
                        {(b.nome || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{b.nome}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {b.categoria_nome}{b.bairro ? ` · ${b.bairro}` : ''}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* categorias */}
          {data.categorias?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase font-semibold text-gray-500 bg-gray-50">Categorias</div>
              {data.categorias.map((c) => (
                <button
                  key={c}
                  role="option"
                  type="button"
                  onClick={() => navCat(c)}
                  data-testid={`autocomplete-cat-${c}`}
                  className="w-full text-left px-3 py-2 hover:bg-purple-50 transition flex items-center gap-2 text-sm"
                >
                  <Tag className="w-3.5 h-3.5 text-purple-600" />
                  <span>{c}</span>
                </button>
              ))}
            </div>
          )}

          {/* bairros */}
          {data.bairros?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase font-semibold text-gray-500 bg-gray-50">Bairros</div>
              {data.bairros.map((b) => (
                <button
                  key={b}
                  role="option"
                  type="button"
                  onClick={() => navBairro(b)}
                  className="w-full text-left px-3 py-2 hover:bg-purple-50 transition flex items-center gap-2 text-sm"
                >
                  <MapPin className="w-3.5 h-3.5 text-amber-600" />
                  <span>{b}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
